import { useEffect, useState } from "react";
import api, { formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Truck, Printer } from "lucide-react";
import { toast } from "sonner";

export default function DeliveryNotes() {
  const [items, setItems] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [parties, setParties] = useState([]);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(null);
  const empty = { invoice_id: "", date: new Date().toISOString().slice(0,10), driver: "", vehicle: "", destination: "", notes: "" };
  const [form, setForm] = useState(empty);

  const load = () => api.get("/delivery-notes").then(r=>setItems(r.data));
  useEffect(()=>{
    load();
    api.get("/invoices?kind=sales").then(r=>setInvoices(r.data));
    api.get("/parties").then(r=>setParties(r.data));
  },[]);

  const save = async () => {
    if (!form.invoice_id) return toast.error("Pilih invoice");
    try { await api.post("/delivery-notes", form); toast.success("Surat jalan dibuat"); setOpen(false); setForm(empty); load(); }
    catch { toast.error("Gagal"); }
  };
  const remove = async (id) => { if(!confirm("Hapus?")) return; await api.delete(`/delivery-notes/${id}`); load(); };
  const partyName = (id) => parties.find(p=>p.id===id)?.name || "-";

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-tag">Akuntansi</div>
          <h1 className="font-display text-4xl tracking-tight mt-1">Surat Jalan</h1>
          <p className="text-sm text-forest-400 mt-2">Dokumen pengiriman berdasarkan invoice penjualan.</p>
        </div>
        <Dialog open={open} onOpenChange={(v)=>{setOpen(v); if(!v)setForm(empty);}}>
          <DialogTrigger asChild>
            <Button data-testid="btn-add-dn" className="rounded-sm bg-[#2D4739] hover:bg-[#22362B] gap-2"><Plus size={16}/> Buat Surat Jalan</Button>
          </DialogTrigger>
          <DialogContent className="bg-[#FDFDFB] rounded-sm max-w-xl">
            <DialogHeader><DialogTitle className="font-display">Buat Surat Jalan</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Berdasarkan Invoice</Label>
                <Select value={form.invoice_id} onValueChange={(v)=>setForm({...form, invoice_id:v})}>
                  <SelectTrigger data-testid="sel-invoice"><SelectValue placeholder="Pilih invoice penjualan" /></SelectTrigger>
                  <SelectContent>{invoices.map(inv=><SelectItem key={inv.id} value={inv.id}>{inv.number} · {partyName(inv.party_id)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Tanggal</Label><Input type="date" value={form.date} onChange={e=>setForm({...form, date:e.target.value})}/></div>
              <div><Label>Sopir</Label><Input value={form.driver||""} onChange={e=>setForm({...form, driver:e.target.value})}/></div>
              <div><Label>Kendaraan</Label><Input value={form.vehicle||""} onChange={e=>setForm({...form, vehicle:e.target.value})}/></div>
              <div><Label>Tujuan</Label><Input value={form.destination||""} onChange={e=>setForm({...form, destination:e.target.value})}/></div>
              <div className="col-span-2"><Label>Catatan</Label><Textarea value={form.notes||""} onChange={e=>setForm({...form, notes:e.target.value})}/></div>
            </div>
            <DialogFooter><Button data-testid="btn-save-dn" onClick={save} className="rounded-sm bg-[#2D4739] hover:bg-[#22362B]">Simpan</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="card-grid overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-forest-100">
            <th className="text-left px-4 py-3 label-tag">No SJ</th>
            <th className="text-left px-4 py-3 label-tag">Invoice</th>
            <th className="text-left px-4 py-3 label-tag">Tanggal</th>
            <th className="text-left px-4 py-3 label-tag">Tujuan</th>
            <th className="text-left px-4 py-3 label-tag">Sopir</th>
            <th className="text-right px-4 py-3 label-tag">Aksi</th>
          </tr></thead>
          <tbody>
            {items.length===0 && <tr><td colSpan={6} className="text-center py-12 text-forest-400">Belum ada surat jalan</td></tr>}
            {items.map(dn => (
              <tr key={dn.id} className="data-row border-b border-forest-100 cursor-pointer" onClick={()=>setView(dn)}>
                <td className="px-4 py-3 font-mono text-xs">{dn.number}</td>
                <td className="px-4 py-3">{dn.invoice_number||"-"}</td>
                <td className="px-4 py-3 text-forest-400">{formatDate(dn.date)}</td>
                <td className="px-4 py-3">{dn.destination||"-"}</td>
                <td className="px-4 py-3">{dn.driver||"-"}</td>
                <td className="px-4 py-3 text-right" onClick={e=>e.stopPropagation()}>
                  <button onClick={()=>window.open(`/print/dn/${dn.id}`,"_blank")} data-testid={`btn-print-dn-${dn.id}`} className="p-1.5 hover:bg-forest-50" title="Cetak"><Printer size={14}/></button>
                  <button onClick={()=>remove(dn.id)} className="p-1.5 hover:bg-forest-50 text-[#C45A3D]"><Trash2 size={14}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!view} onOpenChange={(v)=>!v && setView(null)}>
        <DialogContent className="bg-[#FDFDFB] rounded-sm max-w-xl">
          {view && <>
            <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Truck size={20}/> {view.number}</DialogTitle></DialogHeader>
            <div className="text-sm space-y-1">
              <div className="flex justify-between text-forest-400"><span>Invoice</span><span className="text-[#151A18]">{view.invoice_number}</span></div>
              <div className="flex justify-between text-forest-400"><span>Tanggal</span><span className="text-[#151A18]">{formatDate(view.date)}</span></div>
              <div className="flex justify-between text-forest-400"><span>Tujuan</span><span className="text-[#151A18]">{view.destination||"-"}</span></div>
              <div className="flex justify-between text-forest-400"><span>Sopir</span><span className="text-[#151A18]">{view.driver||"-"}</span></div>
              <div className="flex justify-between text-forest-400"><span>Kendaraan</span><span className="text-[#151A18]">{view.vehicle||"-"}</span></div>
              <table className="w-full mt-4 border-t border-forest-100">
                <thead><tr className="border-b border-forest-100"><th className="text-left py-2 label-tag">Item</th><th className="text-right label-tag">Qty</th></tr></thead>
                <tbody>{view.items?.map((it,i)=>(<tr key={i} className="border-b border-forest-100"><td className="py-2">{it.name}</td><td className="text-right num">{it.qty}</td></tr>))}</tbody>
              </table>
            </div>
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
